import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminLeavesPage.css";
import "../styles/AdminTeamLeavesPage.css";
import "../styles/AdminMyTeamPage.css";
import { useI18n } from "../providers/I18nProvider";
import PaginationControls from "./PaginationControls";
import { apiClient } from "../services/apiClient";
import { normalizeRoleName } from "../utils/roles";
import { useAuth } from "../contexts/AuthContext";
import TeamLeaveCalendarCard from "./TeamLeaveCalendarCard";
import { TabBar, FilterBar, SearchInput, EmptyState, countBy } from "./ListToolbar";

const LOW_BALANCE_DAYS = 3;

const toDateKey = (value) => (value ? String(value).slice(0, 10) : "");

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = d.getTime() - today.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
};

const MyTeamPanel = ({ mode = "page" }) => {
  const { t, lang } = useI18n();
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [upcoming, setUpcoming] = useState([]);
  const [upcomingError, setUpcomingError] = useState("");

  const pageSizeKey = "pageSize.myTeam";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  const [tab, setTab] = useState("all"); // all | onLeave | lowBalance
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [sortKey, setSortKey] = useState("name_asc");
  const loadedOnceRef = React.useRef(false);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const prettyRole = (value) => {
    const normalized = normalizeRoleName(value);
    const map = {
      company_manager: t("team.roles.companyManager"),
      hr: t("team.roles.hr"),
      unit_supervisor: t("team.roles.unitSupervisor"),
      employee: t("team.roles.employee"),
      it: t("team.roles.it"),
    };
    return map[normalized] || normalized || "—";
  };

  const fetchAll = async () => {
    setError("");
    try {
      const usersRes = await apiClient.get("/users");
      const everyone = usersRes.data || [];
      // Kendimizi render sırasında ayıklıyoruz: auth bilgisi gelmeden yüklenirsek de doğru çalışsın.
      const sorted = [...everyone].sort((a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, lang === "tr" ? "tr-TR" : "en-US", {
          sensitivity: "base",
        })
      );
      setMembers(sorted);
      loadedOnceRef.current = true;
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || t("common.genericError");
      if (loadedOnceRef.current) setUpcomingError(msg);
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const role = normalizeRoleName(me?.role_name);
    const canSeeReminders =
      role === "admin" || role === "unit_supervisor" || role === "company_manager";
    if (!canSeeReminders) {
      setUpcoming([]);
      setUpcomingError("");
      return;
    }

    setUpcomingError("");
    apiClient
      .get("/admin/leaves/upcoming", { params: { days: 14, limit: 10 } })
      .then((res) => setUpcoming(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        setUpcoming([]);
        setUpcomingError(err.response?.data?.msg || err.message || t("common.genericError"));
      });
  }, [me?.role_name, t]);

  const teamMembers = useMemo(
    () => (me?.id ? members.filter((u) => u && u.id !== me.id) : members),
    [members, me?.id]
  );

  const upcomingForMembers = useMemo(() => {
    if (!upcoming.length) return [];
    const ids = new Set(teamMembers.map((m) => Number(m?.id)).filter((x) => Number.isFinite(x)));
    return upcoming.filter((l) => ids.has(Number(l.user_id)));
  }, [teamMembers, upcoming]);

  const onLeaveIds = useMemo(
    () => new Set(upcomingForMembers.map((l) => Number(l.user_id))),
    [upcomingForMembers]
  );

  const isLowBalance = (m) => {
    const v = m.total_leave_quota_days;
    return v !== null && v !== undefined && v !== "" && Number(v) <= LOW_BALANCE_DAYS;
  };

  const tabCounts = useMemo(
    () =>
      countBy(teamMembers, {
        all: () => true,
        onLeave: (m) => onLeaveIds.has(Number(m.id)),
        lowBalance: (m) => isLowBalance(m),
      }),
    [teamMembers, onLeaveIds]
  );

  const roleOptions = useMemo(
    () => [...new Set(teamMembers.map((m) => normalizeRoleName(m.role_name)).filter(Boolean))],
    [teamMembers]
  );
  const teamOptions = useMemo(
    () =>
      [...new Set(teamMembers.map((m) => m.team_name).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, lang === "tr" ? "tr-TR" : "en-US")
      ),
    [teamMembers, lang]
  );

  const filteredMembers = useMemo(() => {
    const locale = lang === "tr" ? "tr-TR" : "en-US";
    const q = search.trim().toLocaleLowerCase(locale);
    const list = teamMembers.filter((m) => {
      if (tab === "onLeave" && !onLeaveIds.has(Number(m.id))) return false;
      if (tab === "lowBalance" && !isLowBalance(m)) return false;
      if (roleFilter && normalizeRoleName(m.role_name) !== roleFilter) return false;
      if (teamFilter && m.team_name !== teamFilter) return false;
      if (q) {
        const hay = `${m.name || ""} ${m.surname || ""} ${m.username || ""} ${m.department || ""} ${
          m.section || ""
        }`.toLocaleLowerCase(locale);
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const bal = (m) =>
      m.total_leave_quota_days === null || m.total_leave_quota_days === undefined || m.total_leave_quota_days === ""
        ? Number.POSITIVE_INFINITY
        : Number(m.total_leave_quota_days);
    const sorters = {
      name_asc: (a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, locale, { sensitivity: "base" }),
      balance_asc: (a, b) => bal(a) - bal(b),
      balance_desc: (a, b) => bal(b) - bal(a),
    };
    return [...list].sort(sorters[sortKey] || sorters.name_asc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMembers, tab, search, roleFilter, teamFilter, sortKey, onLeaveIds, lang]);

  const hasActiveFilter = Boolean(search || roleFilter || teamFilter);
  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setTeamFilter("");
  };

  useEffect(() => {
    setPage(1);
  }, [tab, search, roleFilter, teamFilter, sortKey]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredMembers.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredMembers.length, page, pageSize]);

  const pagedMembers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredMembers.slice(start, start + pageSize);
  }, [filteredMembers, page, pageSize]);

  if (loading) return <div className="my-team-page">{t("common.loading")}</div>;
  if (error) return <div className="my-team-page error">{t("common.error")}: {error}</div>;

  const showHeader = mode === "page";

  return (
    <div className={mode === "page" ? "my-team-page" : "my-team-page embedded"}>
      {showHeader && (
        <div className="team-leaves-header">
          <div>
            <div className="eyebrow">{t("team.eyebrow")}</div>
            <h2 className="admin-title">{t("team.title")}</h2>
            <p className="muted">{t("team.subtitle")}</p>
          </div>
          <div className="actions">
            <button className="ghost" type="button" onClick={fetchAll}>
              {t("team.refresh")}
            </button>
          </div>
        </div>
      )}

      {mode === "page" ? <TeamLeaveCalendarCard me={me} /> : null}

      {upcomingError ? (
        <div className="card">
          <div className="card-head">
            <h3>{t("team.upcomingApprovedTitle")}</h3>
          </div>
          <div className="muted">{upcomingError}</div>
        </div>
      ) : upcomingForMembers.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h3>{t("team.upcomingApprovedTitle")}</h3>
          </div>
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("team.columns.name")}</th>
                  <th>{t("admin.leaves.columns.type")}</th>
                  <th>{t("admin.leaves.start")}</th>
                  <th>{t("team.upcomingInDays")}</th>
                </tr>
              </thead>
              <tbody>
                {upcomingForMembers.slice(0, 8).map((l) => {
                  const when = daysUntil(l.start_date);
                  return (
                    <tr key={l.id}>
                      <td className="cell-strong">
                        {l.user_name} {l.user_surname} {l.user_username ? `(${l.user_username})` : ""}
                      </td>
                      <td>
                        {l.leave_sub_type_name
                          ? `${l.leave_type_name} (${l.leave_sub_type_name})`
                          : l.leave_type_name}
                      </td>
                      <td className="mono">{toDateKey(l.start_date)}</td>
                      <td>{when === null ? "—" : t("team.inDays", { days: when })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {teamMembers.length === 0 ? (
        <EmptyState message={t("team.empty")} />
      ) : (
        <div className="admin-leaves-container">
          <TabBar
            value={tab}
            onChange={setTab}
            ariaLabel={t("team.title")}
            tabs={[
              { key: "all", label: t("myTeamPage.tabs.all"), count: tabCounts.all },
              ...(upcoming.length > 0 || tab === "onLeave"
                ? [{ key: "onLeave", label: t("myTeamPage.tabs.onLeave"), count: tabCounts.onLeave }]
                : []),
              { key: "lowBalance", label: t("myTeamPage.tabs.lowBalance"), count: tabCounts.lowBalance, attention: true },
            ]}
          />
          <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
            <SearchInput value={search} onChange={setSearch} placeholder={t("myTeamPage.search")} />
            {roleOptions.length > 1 && (
              <select
                className="leaves-filter-input lt-input"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label={t("team.columns.role")}
              >
                <option value="">{t("myTeamPage.allRoles")}</option>
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {prettyRole(r)}
                  </option>
                ))}
              </select>
            )}
            {teamOptions.length > 1 && (
              <select
                className="leaves-filter-input lt-input"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                aria-label={t("team.columns.team")}
              >
                <option value="">{t("teamLeaves.filters.allTeams")}</option>
                {teamOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
            <select
              className="leaves-filter-input lt-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("admin.leaves.filters.sort")}
            >
              <option value="name_asc">{t("teamLeaves.filters.sortName")}</option>
              <option value="balance_asc">{t("myTeamPage.sortBalanceAsc")}</option>
              <option value="balance_desc">{t("myTeamPage.sortBalanceDesc")}</option>
            </select>
          </FilterBar>
          {filteredMembers.length === 0 && <EmptyState message={t("admin.leaves.emptyFiltered")} />}
          <PaginationControls
            totalItems={filteredMembers.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("team.columns.name")}</th>
                  <th>{t("team.columns.username")}</th>
                  <th>{t("team.columns.role")}</th>
                  <th>{t("team.columns.team")}</th>
                  <th>{t("team.columns.department")}</th>
                  <th>{t("team.columns.manager")}</th>
                  <th>{t("team.columns.annualLeaveBalance")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedMembers.map((member) => {
                  const bal = member.total_leave_quota_days;
                  const balText =
                    bal === null || bal === undefined || bal === ""
                      ? "—"
                      : `${Number(bal)} ${t("leave.summary.days")}`;
                  return (
                    <tr key={member.id}>
                      <td className="cell-strong">
                        {member.name} {member.surname}
                        {onLeaveIds.has(Number(member.id)) && (
                          <span className="chain-chip chain-beklemede member-chip">
                            {t("myTeamPage.upcomingLeave")}
                          </span>
                        )}
                      </td>
                      <td>{member.username}</td>
                      <td>{prettyRole(member.role_name)}</td>
                      <td>{member.team_name || "—"}</td>
                      <td>
                        {member.department || member.section
                          ? `${member.department || "-"} / ${member.section || "-"}`
                          : "-"}
                      </td>
                      <td>
                        {member.manager_username
                          ? `${member.manager_name || ""} ${member.manager_surname || ""}`.trim() ||
                            member.manager_username
                          : "—"}
                      </td>
                      <td className={`mono${isLowBalance(member) ? " low-balance" : ""}`}>{balText}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls
            totalItems={filteredMembers.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  );
};

export default MyTeamPanel;
